import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Cloud, HardDrive, Loader2, Server } from 'lucide-react'

import { storageApi, type SilkUserCustomStorageConfig } from '@/api/storage'
import { createProduction } from '@/api/studio-production'
import { AuthSessionExpiredError } from '@/api/authSession'
import { MEMBER_ROLES, PROJECT_TYPES, type MemberRole, type StorageMode } from '@/features/studio/lib/production-constants'
import { productionBasePath } from '@/features/studio/lib/production-path'
import { formatResolvedApiError, resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { ProtoBtn, ProtoInput, ProtoLabel, ProtoModal, ProtoNumberInput, ProtoSelect, ProtoTextarea } from '@/features/studio/components/ui-kit'
import { Button } from '@/shared/components/ui/button'
import { toast } from '@/shared/lib/notify'

export function CreateProductionModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated?: () => void
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [projectType, setProjectType] = useState<string>('comic_drama')
  const [aspectRatio, setAspectRatio] = useState('9:16')
  const [episodeTarget, setEpisodeTarget] = useState(1)
  const [defaultLocale, setDefaultLocale] = useState('zh-CN')
  const [creatorRole, setCreatorRole] = useState<MemberRole>('director')
  const [storage, setStorage] = useState<StorageMode>('tenant_oss')
  const [customConfigured, setCustomConfigured] = useState(false)
  const [customSummary, setCustomSummary] = useState<SilkUserCustomStorageConfig | null>(null)
  const [customLoading, setCustomLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setError(null)
  }, [open])

  useEffect(() => {
    if (!open || storage !== 'custom') return
    let cancelled = false
    setCustomLoading(true)
    void storageApi
      .getCustomConfig()
      .then((res) => {
        if (cancelled) return
        setCustomConfigured(Boolean(res.configured))
        setCustomSummary(res.storage_config ?? null)
      })
      .catch((e) => {
        if (cancelled) return
        if (e instanceof AuthSessionExpiredError) return
        setCustomConfigured(false)
        setCustomSummary(null)
      })
      .finally(() => {
        if (!cancelled) setCustomLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, storage])

  async function handleSubmit() {
    const trimmed = title.trim()
    if (!trimmed) {
      setError(t('createModal.titleRequired'))
      return
    }
    if (storage === 'custom') {
      if (customLoading) return
      if (!customConfigured) {
        const msg = t('createModal.storageCustomMissing')
        setError(msg)
        toast.warning(msg)
        return
      }
    }
    setSubmitting(true)
    setError(null)
    try {
      const row = await createProduction({
        title: trimmed,
        description: description.trim() || undefined,
        aspect_ratio: aspectRatio,
        episode_target: episodeTarget,
        project_type: projectType,
        default_locale: defaultLocale,
        storage_mode: storage,
        creator_role: creatorRole,
      })
      onClose()
      onCreated?.()
      navigate(productionBasePath(row.public_id))
    } catch (e) {
      if (e instanceof AuthSessionExpiredError) return
      const resolved = resolveApiErrorMessage(e, 'hub.createFailed')
      const message = formatResolvedApiError(t, resolved)
      setError(message)
      toast.error(message)
    } finally {
      setSubmitting(false)
    }
  }

  const providerLabel =
    customSummary?.provider === 'tencent_cos'
      ? t('storageConfig.providerTencent')
      : t('storageConfig.providerAliyun')

  const customReady = storage === 'custom' && customConfigured && !customLoading
  const customBlocked = storage === 'custom' && !customLoading && !customConfigured

  return (
    <ProtoModal
      open={open}
      onClose={onClose}
      title={t('createModal.title')}
      wide
      footer={
        <>
          <ProtoBtn size="sm" variant="secondary" onClick={onClose} disabled={submitting}>
            {t('common.close')}
          </ProtoBtn>
          <ProtoBtn
            size="sm"
            onClick={() => void handleSubmit()}
            disabled={submitting || customLoading || customBlocked}
          >
            {submitting ? t('createModal.submitting') : t('createModal.submit')}
          </ProtoBtn>
        </>
      }
    >
      <div className="space-y-6">
        {error ? (
          <p className="rounded-xl border border-rose-500/25 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
            {error}
          </p>
        ) : null}

        <section>
          <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">
            {t('createModal.sectionBasic')}
          </h4>
          <div className="space-y-3">
            <div>
              <ProtoLabel required>{t('createModal.fieldTitle')}</ProtoLabel>
              <ProtoInput
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('createModal.fieldTitlePlaceholder')}
              />
            </div>
            <div>
              <ProtoLabel>{t('createModal.fieldDescription')}</ProtoLabel>
              <ProtoTextarea
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <ProtoLabel required>{t('createModal.fieldType')}</ProtoLabel>
                <ProtoSelect value={projectType} onChange={(e) => setProjectType(e.target.value)}>
                  {PROJECT_TYPES.map((k) => (
                    <option key={k} value={k}>
                      {t(`production.projectType.${k}`, { defaultValue: k })}
                    </option>
                  ))}
                </ProtoSelect>
              </div>
              <div>
                <ProtoLabel required>{t('createModal.fieldAspect')}</ProtoLabel>
                <ProtoSelect value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value)}>
                  <option value="9:16">9:16</option>
                  <option value="16:9">16:9</option>
                  <option value="1:1">1:1</option>
                  <option value="2.39:1">2.39:1</option>
                </ProtoSelect>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <ProtoLabel required>{t('createModal.fieldEpisodes')}</ProtoLabel>
                <ProtoNumberInput min={1} value={episodeTarget} onValueChange={setEpisodeTarget} />
              </div>
              <div>
                <ProtoLabel required>{t('createModal.fieldLocale')}</ProtoLabel>
                <ProtoSelect value={defaultLocale} onChange={(e) => setDefaultLocale(e.target.value)}>
                  <option value="zh-CN">简体中文</option>
                  <option value="en-US">English</option>
                </ProtoSelect>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">
            {t('createModal.sectionStorage')}
          </h4>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setStorage('tenant_oss')}
              className={`h-auto justify-start rounded-xl p-3 text-left transition ${storage === 'tenant_oss'
                  ? 'border-indigo-500/40 bg-indigo-500/10'
                  : 'border-border hover:border-muted-foreground/50'
                }`}
            >
              <Cloud className="mb-2 h-5 w-5 text-sky-400" />
              <p className="text-sm font-medium text-zinc-200">{t('createModal.storageTenant')}</p>
              <p className="mt-0.5 text-[11px] text-zinc-500">{t('createModal.storageTenantHint')}</p>
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setStorage('custom')}
              className={`h-auto justify-start rounded-xl p-3 text-left transition ${storage === 'custom'
                  ? 'border-indigo-500/40 bg-indigo-500/10'
                  : 'border-border hover:border-muted-foreground/50'
                }`}
            >
              <Server className="mb-2 h-5 w-5 text-violet-400" />
              <p className="text-sm font-medium text-zinc-200">{t('createModal.storageCustom')}</p>
              <p className="mt-0.5 text-[11px] text-zinc-500">{t('createModal.storageCustomHint')}</p>
            </Button>
          </div>
          {storage === 'custom' ? (
            <div className="mt-3 rounded-xl border border-white/[0.06] bg-[#0e0e16] p-4">
              {customLoading ? (
                <p className="flex items-center gap-2 text-xs text-zinc-400">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {t('common.loading')}
                </p>
              ) : customReady ? (
                <p className="text-xs text-emerald-200/90">
                  {t('createModal.storageCustomReady', {
                    provider: providerLabel,
                    bucket: customSummary?.bucket || '—',
                    region: customSummary?.region || '—',
                  })}
                </p>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs text-amber-100/90">{t('createModal.storageCustomMissing')}</p>
                  <Button asChild size="sm" variant="indigo">
                    <Link to="/account/storage" onClick={onClose}>
                      {t('createModal.storageCustomGoConfig')}
                    </Link>
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="mt-2 flex items-center gap-2 text-[11px] text-zinc-600">
              <HardDrive className="h-3.5 w-3.5" />
              {t('createModal.storageLocalNote')}
            </div>
          )}
        </section>

        <section>
          <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">
            {t('createModal.sectionRole')}
          </h4>
          <ProtoLabel required>{t('createModal.fieldCreatorRole')}</ProtoLabel>
          <ProtoSelect
            value={creatorRole}
            onChange={(e) => setCreatorRole(e.target.value as MemberRole)}
          >
            {MEMBER_ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`production.roles.${r}`, { defaultValue: r })}
              </option>
            ))}
          </ProtoSelect>
        </section>
      </div>
    </ProtoModal>
  )
}
